import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import billingService from '../../services/billingService';
import { errText } from '../../services/scheduleService';
import { isAdmin } from '../../utils/permissions';
import InvoicePreview from '../../components/billing/InvoicePreview';
import { statusOf } from '../../components/billing/ClaimTracker';
import { rands } from '../../components/billing/TariffLines';

const TABS = [
  { key: 'review', label: 'To review', hint: 'Sent by the doctor — check and submit' },
  { key: 'queries', label: 'With doctor', hint: 'Waiting for the doctor to answer a question' },
  { key: 'drafts', label: 'Drafts', hint: 'The doctor hasn’t sent these to billing yet' },
  { key: 'submitted', label: 'Submitted', hint: 'Waiting for the medical aid or patient' },
  { key: 'accepted', label: 'Accepted', hint: 'Accepted or part paid' },
  { key: 'rejected', label: 'Rejected', hint: 'Needs attention' },
  { key: 'paid', label: 'Paid', hint: 'Settled in full' },
  { key: 'all', label: 'All', hint: 'Everything except discarded' },
];
const TAB_TONE = {
  review: 'text-indigo-700', queries: 'text-rose-700', drafts: 'text-amber-700', submitted: 'text-blue-700',
  accepted: 'text-teal-700', rejected: 'text-red-700', paid: 'text-green-700', all: 'text-slate-700',
};
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const fmtWhen = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' }) : '—');

/**
 * Billing & Claims — the billing team's workbench. Doctors capture cases on their
 * phone and send them here; the team reviews them (To review), asks the doctor
 * when something is unclear (With doctor), approves and submits, then tracks the
 * claim to paid. SIMULATED until a claims switch is connected.
 */
const BillingClaims = () => {
  const admin = isAdmin();
  const location = useLocation(); // the dashboard can open a tab or one invoice directly
  const [tab, setTab] = useState(() => location.state?.tab || (admin ? 'review' : 'drafts'));
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(() => location.state?.openId || null);

  const load = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([billingService.list({ bucket: tab, q }), billingService.summary()]);
      setRows(list);
      setSummary(sum);
      setError('');
    } catch (e) {
      setError(errText(e));
    }
  }, [tab, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  return (
    <div className="mx-auto max-w-7xl p-8">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-brand-deep">Billing &amp; Claims</h1>
          <p className="mt-1 text-slate-500">{admin ? 'Every invoice in the practice' : 'Your invoices'}, from the doctor’s capture to paid.</p>
        </div>
        {summary && (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-right shadow-sm">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Still owed on claims</div>
            <div className="text-xl font-bold text-brand-deep">{rands(summary.outstanding)}</div>
          </div>
        )}
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900">
        <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0" />
        <span>
          Doctors send cases here from their phone. Review each one, correct it if needed (the doctor is told), ask the doctor if something is unclear, then <b>Approve &amp; submit</b>.
          No claims switch is connected yet, so submission is simulated — record acceptances, rejections and payments on the invoice.
        </span>
      </div>

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            title={tb.hint}
            className={`rounded-xl border px-3 py-2.5 text-left transition ${tab === tb.key ? 'border-brand-navy bg-white shadow-sm ring-2 ring-brand-navy/10' : 'border-slate-200 bg-white/70 hover:border-slate-300'}`}
          >
            <div className={`text-xs font-bold ${TAB_TONE[tb.key]}`}>{tb.label}</div>
            <div className="text-lg font-bold text-slate-800">{summary ? summary[tb.key].count : '—'}</div>
            <div className="text-[11px] text-slate-500">{summary ? rands(summary[tb.key].total) : ''}</div>
          </button>
        ))}
      </div>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
          <div className="relative max-w-sm flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Patient, invoice number, member number or procedure…" className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm" />
          </div>
          <div className="flex-1" />
          <button onClick={load} title="Refresh" className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-brand-navy"><ArrowPathIcon className="h-5 w-5" /></button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2 font-semibold">Invoice</th>
                <th className="px-2 py-2 font-semibold">Service date</th>
                <th className="px-2 py-2 font-semibold">Patient</th>
                {admin && <th className="px-2 py-2 font-semibold">Doctor</th>}
                <th className="px-2 py-2 font-semibold">ICD-10</th>
                <th className="px-2 py-2 text-right font-semibold">Total</th>
                <th className="px-2 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 text-right font-semibold">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(rows || []).map((r) => {
                const s = statusOf(r);
                return (
                  <tr key={r.draft_id} onClick={() => setOpenId(r.draft_id)} className={`cursor-pointer align-top hover:bg-slate-50 ${openId === r.draft_id ? 'bg-teal-50/60' : ''}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-mono font-semibold text-brand-navy">{r.invoice_no || `#${r.draft_id}`}</div>
                      {r.tariff_source_kind && r.tariff_source_kind !== 'licensed' && <div className="text-[10px] font-semibold text-amber-700">placeholder tariffs</div>}
                    </td>
                    <td className="px-2 py-2.5 text-slate-600">{fmtDate(r.service_date)}</td>
                    <td className="px-2 py-2.5">
                      <div className="font-semibold text-slate-700">{r.patient_name || <span className="text-slate-400">No patient linked</span>}</div>
                      <div className="text-[11px] text-slate-500">{r.payer === 'private' ? 'Private' : r.scheme_name ? `${r.scheme_name}${r.membership_number ? ` · ${r.membership_number}` : ''}` : ''}</div>
                      {r.procedure_text && <div className="max-w-xs truncate text-[11px] text-slate-400">{r.procedure_text}</div>}
                      {r.status === 'query' && r.query_note && <div className="max-w-xs truncate text-[11px] font-semibold text-rose-700">Q: {r.query_note}</div>}
                    </td>
                    {admin && <td className="px-2 py-2.5 text-slate-600">{r.doctor}</td>}
                    <td className="px-2 py-2.5 font-mono text-xs text-slate-600">{r.icd10_codes || '—'}</td>
                    <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums">
                      <div className="font-semibold text-slate-800">{r.total == null ? '—' : rands(r.total)}</div>
                      {r.amount_paid > 0 && <div className="text-[11px] text-green-700">paid {rands(r.amount_paid)}</div>}
                    </td>
                    <td className="px-2 py-2.5"><span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold ${s.tone}`}>{s.short}</span></td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-500">{fmtWhen(r.claim_status_at || r.query_at || r.sent_for_review_at || r.created_at)}</td>
                  </tr>
                );
              })}
              {rows && rows.length === 0 && (
                <tr><td colSpan={admin ? 8 : 7} className="px-4 py-8 text-center text-slate-400">Nothing here{q ? ' matches that search' : ''}.</td></tr>
              )}
              {rows === null && <tr><td colSpan={admin ? 8 : 7} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {openId && <InvoiceDrawer id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
};

const InvoiceDrawer = ({ id, onClose, onChanged }) => {
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    setDraft(null);
    billingService.draft(id).then(setDraft).catch((e) => setErr(errText(e)));
  }, [id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-brand-deep/30" onMouseDown={onClose}>
      <div className="flex h-full w-full max-w-3xl flex-col bg-slate-50 shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
          <h2 className="font-bold text-brand-deep">Invoice</h2>
          <button onClick={onClose} title="Close (Esc)" className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {err && <p className="text-sm text-red-600">{err}</p>}
          {!draft && !err && <p className="text-sm text-slate-400">Loading…</p>}
          {draft && <InvoicePreview draft={draft} trackLink={false} onChange={() => onChanged()} />}
        </div>
      </div>
    </div>
  );
};

export default BillingClaims;
